import { useRouter } from 'next/router'

export const useNavigationContext = () => {
  const router = useRouter()
  const asPathWithoutQuery = router.asPath.split('?')[0]
  const { query } = router

  const inSupportContext = router.pathname.startsWith('/support')

  const organisationId = query?.id && parseInt(`${query.id}`)
  const folderId = query?.folderId && parseInt(`${query.folderId}`)
  const rootDocumentId = query?.rootDocumentId
    ? parseInt(`${query.rootDocumentId}`)
    : null
  const documentId = query?.documentId && parseInt(`${query.documentId}`)

  const accessToken = query?.accessToken && `${query.accessToken}`
  const validUntil = query?.validUntil && `${query.validUntil}`

  const inBusinessUnitContext = asPathWithoutQuery.includes('/business-units')
  const businessUnitId =
    query?.businessUnitId && parseInt(`${query.businessUnitId}`)

  const inProjectContext = asPathWithoutQuery.includes('/projects')
  const projectId = query?.projectId && parseInt(`${query.projectId}`)

  const inPermitContext = asPathWithoutQuery.includes('/permits')
  const permitId = query?.permitId && parseInt(`${query.permitId}`)
  const parentPermitId =
    query?.parentPermitId && parseInt(`${query.parentPermitId}`)

  const inTemplateContext = asPathWithoutQuery.includes('/templates')

  const templateId = query?.templateid && parseInt(`${query.templateid}`)

  const inChecklistVersionContext =
    (asPathWithoutQuery.includes('/checklist-templates') ||
      asPathWithoutQuery.includes('/inspection-templates')) &&
    asPathWithoutQuery.includes('/versions')
  const checklistId = query?.checklistId && parseInt(`${query.checklistId}`)
  const checklistTemplateId =
    query?.checklistTemplateId && parseInt(`${query?.checklistTemplateId}`)
  const checklistTemplateVersionId =
    inChecklistVersionContext &&
    query?.versionId &&
    parseInt(`${query.versionId}`)

  const inTemplateVersionContext =
    asPathWithoutQuery.includes('/templates') &&
    asPathWithoutQuery.includes('/versions')
  const templateVersionId = query?.version && parseInt(`${query.version}`)

  const inRegisterDiagramsContext = asPathWithoutQuery.includes('/diagrams')

  const inRegisterTemplateContext = asPathWithoutQuery.includes(
    '/register-templates',
  )

  const registerTemplateId =
    inRegisterTemplateContext && parseInt(`${query?.templateId}`)

  const inTaskTemplateContext = asPathWithoutQuery.includes('/task-templates')

  const taskTemplateId =
    inTaskTemplateContext && parseInt(`${query?.templateId}`)

  const taskTemplateVersionId =
    inTaskTemplateContext && parseInt(`${query?.versionId}`)

  const inDocumentEngineTemplateContext = asPathWithoutQuery.includes(
    '/document-engine-templates',
  )

  const documentEngineTemplateId =
    inDocumentEngineTemplateContext &&
    query?.documentEngineTemplateId &&
    parseInt(`${query.documentEngineTemplateId}`)

  const inDocumentEngineTemplateVersionContext =
    inDocumentEngineTemplateContext && asPathWithoutQuery.includes('/versions')

  const documentEngineTemplateVersionId =
    inDocumentEngineTemplateVersionContext &&
    query?.versionId &&
    parseInt(`${query.versionId}`)

  const taskId = query?.taskId && parseInt(`${query.taskId}`)

  const userId = query?.userId && parseInt(`${query.userId}`)

  const authorisationGroupId = query?.groupId && parseInt(`${query.groupId}`)
  const dashboardId = query?.dashboardId && parseInt(`${query?.dashboardId}`)
  const inAnalyticsContext = asPathWithoutQuery.includes('/analytics')
  const inTimelineContext = asPathWithoutQuery.includes('/timeline')
  const inTasksContext = asPathWithoutQuery.includes('/tasks')
  const inObservationsContext = asPathWithoutQuery.includes('/observations')
  const inUsersContext = asPathWithoutQuery.includes('/users')
  const inChecklistsContext = asPathWithoutQuery.includes('/checklists')
  const inInspectionsContext = /\/inspections(\/|$)/.test(asPathWithoutQuery)
  const inRegistersContext = asPathWithoutQuery.includes('/registers')
  const inSettingsContext = asPathWithoutQuery.includes('/settings')
  const inAuthorisationGroupsContext =
    asPathWithoutQuery.includes('/settings/groups')
  const inCompaniesContext = asPathWithoutQuery.includes('/settings/companies')
  const inOrganisationCompaniesContext =
    asPathWithoutQuery.includes('/companies') &&
    !asPathWithoutQuery.includes('/settings/companies')
  const companyId = query?.companyId && parseInt(`${query.companyId}`)
  const inPermitTemplatesContext = asPathWithoutQuery.includes(
    '/settings/permit-templates',
  )
  const inChecklistTemplatesContext = asPathWithoutQuery.includes(
    '/settings/checklist-templates',
  )
  const inInspectionTemplatesContext = asPathWithoutQuery.includes(
    '/settings/inspection-templates',
  )
  const inCompetencyTemplatesContext = asPathWithoutQuery.includes(
    '/settings/competency-templates',
  )
  const competencyTemplateId =
    inCompetencyTemplatesContext && parseInt(`${query?.templateId}`)
  const inCompetencyTemplateVersionContext =
    inCompetencyTemplatesContext && asPathWithoutQuery.includes('/versions')
  const competencyTemplateVersionId =
    inCompetencyTemplateVersionContext && parseInt(`${query?.versionId}`)
  const inTagsContext = asPathWithoutQuery.includes('/settings/tags')
  const inSiteLocationsContext = asPathWithoutQuery.includes(
    '/settings/locations',
  )
  const inMapContext = asPathWithoutQuery.includes('/settings/map')
  const inProjectMapContext =
    asPathWithoutQuery.includes('/projects') &&
    asPathWithoutQuery.endsWith('/map')
  const inPlansContext =
    asPathWithoutQuery.includes('/projects') &&
    asPathWithoutQuery.includes('/view-plans')
  const inSiteBoxContext = asPathWithoutQuery.includes('/site-box')
  const inOrganisationHome = router.pathname === '/organisations/[id]'
  const inProjectOverview =
    router.pathname === '/organisations/[id]/projects/[projectId]/overview'
  const inBusinessUnitOverview =
    router.pathname ===
    '/organisations/[id]/business-units/[businessUnitId]/overview'

  const adminUserId = query?.adminUserId && parseInt(`${query?.adminUserId}`)

  const registerId = query?.registerId && parseInt(`${query.registerId}`)
  const registerItemId =
    query?.registerItemId && parseInt(`${query.registerItemId}`)
  const scheduledInspectionId =
    query?.scheduledInspectionId && parseInt(`${query.scheduledInspectionId}`)
  const registerItemViewId =
    registerItemId && query?.viewId && parseInt(`${query.viewId}`)

  const nodeId = query?.nodeId && query.nodeId
  const planId = query?.planId && parseInt(`${query.planId}`)
  const nodePath = query?.nodePath && query.nodePath
  const locationArchived = query?.locationArchived && Boolean(query.locationId)

  const inProjectSettingsContext =
    asPathWithoutQuery.includes('/projects') &&
    asPathWithoutQuery.includes('/settings')

  const inOrganisationSettingsContext =
    asPathWithoutQuery.includes('/settings') && !inProjectSettingsContext

  const registerTemplateInspectionId =
    query?.registerTemplateInspectionId &&
    `${query.registerTemplateInspectionId}`
  const inspectionTemplateId =
    query?.inspectionTemplateId && parseInt(`${query.inspectionTemplateId}`)

  const versionId = query?.versionId && parseInt(`${query.versionId}`)

  return {
    asPathWithoutQuery,
    inSupportContext,
    organisationId,
    inProjectContext,
    projectId,
    inTemplateContext,
    templateId,
    inPermitContext,
    permitId,
    parentPermitId,
    inBusinessUnitContext,
    businessUnitId,
    inTemplateVersionContext,
    templateVersionId,
    userId,
    authorisationGroupId,
    folderId,
    rootDocumentId,
    documentId,
    accessToken,
    validUntil,
    checklistId,
    checklistTemplateId,
    checklistTemplateVersionId,
    inChecklistVersionContext,
    dashboardId,
    inAnalyticsContext,
    inTimelineContext,
    inTasksContext,
    inObservationsContext,
    inUsersContext,
    inChecklistsContext,
    inInspectionsContext,
    inRegistersContext,
    inSettingsContext,
    inAuthorisationGroupsContext,
    inCompaniesContext,
    inOrganisationCompaniesContext,
    companyId,
    inPermitTemplatesContext,
    inChecklistTemplatesContext,
    inInspectionTemplatesContext,
    inTagsContext,
    inSiteLocationsContext,
    inMapContext,
    inProjectMapContext,
    inPlansContext,
    inSiteBoxContext,
    inOrganisationHome,
    inProjectOverview,
    inBusinessUnitOverview,
    adminUserId,
    registerId,
    registerItemId,
    scheduledInspectionId,
    registerItemViewId,
    nodeId,
    planId,
    nodePath,
    locationArchived,
    inRegisterTemplateContext,
    registerTemplateId,
    inTaskTemplateContext,
    taskTemplateId,
    taskTemplateVersionId,
    taskId,
    inProjectSettingsContext,
    inOrganisationSettingsContext,
    inRegisterDiagramsContext,
    registerTemplateInspectionId,
    inspectionTemplateId,
    versionId,
    inDocumentEngineTemplateContext,
    documentEngineTemplateId,
    inDocumentEngineTemplateVersionContext,
    documentEngineTemplateVersionId,
    inCompetencyTemplatesContext,
    competencyTemplateId,
    inCompetencyTemplateVersionContext,
    competencyTemplateVersionId,
  }
}
